import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CardDeck } from '../review/CardDeck';
import { reviewSummary } from '../review';
import { readRememberedReviewSession, useReviewStore, rememberReviewSession } from '../session/review';
import { useSessionStore } from '../session/store';
import { useTokenSource } from '../api/token';

/**
 * Review route — where the AI's proposals become the coach's decisions.
 *
 * A session can arrive three ways, in this order: `?session=<id>` (a link), the id remembered
 * from the last review, or the id the capture screen just created. All three end in the same
 * place: GET /api/sessions/:id, which returns the session AND its clips, so the deck is restored
 * from D1 rather than from memory.
 */
export function ReviewRoute() {
  const tokens = useTokenSource();
  const [params, setParams] = useSearchParams();
  const storeSessionId = useSessionStore((state) => state.sessionId);
  const sessionId = useReviewStore((state) => state.sessionId);
  const session = useReviewStore((state) => state.session);
  const clips = useReviewStore((state) => state.clips);
  const loading = useReviewStore((state) => state.loading);
  const error = useReviewStore((state) => state.error);
  const load = useReviewStore((state) => state.load);

  const [token, setToken] = useState<string | null>(null);
  // 0 = the token source has not resolved yet. Fetching before it does would send an
  // unauthenticated request and then have nothing left to trigger the retry — the token
  // resolving is the event that starts (or restarts) the load.
  const [tokenAttempt, setTokenAttempt] = useState(0);
  const [manualId, setManualId] = useState('');

  useEffect(() => {
    let cancelled = false;
    void tokens.getToken().then((value) => {
      if (cancelled) return;
      setToken(value);
      setTokenAttempt((attempt) => attempt + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [tokens]);

  const requestedId =
    params.get('session') ?? readRememberedReviewSession() ?? storeSessionId ?? null;

  useEffect(() => {
    if (!requestedId || tokenAttempt === 0) return;
    if (sessionId === requestedId && !error) return;
    void load(requestedId, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedId, tokenAttempt]);

  // E2E / integration seam: the deck's state is inspectable without scraping the DOM.
  useEffect(() => {
    const seam = {
      store: useReviewStore,
      summary: () => reviewSummary(useReviewStore.getState().clips),
      load,
      tokenSource: tokens.mode,
      tokenPresent: token !== null,
    };
    (window as unknown as { __calloutReview?: unknown }).__calloutReview = seam;
    return () => {
      delete (window as unknown as { __calloutReview?: unknown }).__calloutReview;
    };
  }, [load, tokens.mode, token]);

  const openSession = useCallback(
    (id: string) => {
      rememberReviewSession(id);
      void load(id, token);
      setParams({ session: id }, { replace: false });
    },
    [load, setParams, token],
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Review</h1>
        <p className="mt-1 text-sm text-charcoal-500">
          The AI proposes, you decide. Nothing is exported until you confirm it.
        </p>
      </header>

      <form
        data-testid="review-session-form"
        className="flex flex-wrap items-end gap-2 rounded-lg border border-charcoal-500/20 bg-white/60 px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          const id = manualId.trim() || requestedId || '';
          if (id) openSession(id);
        }}
      >
        <label className="text-xs font-medium text-charcoal-700">
          Session id
          <input
            data-testid="review-session-input"
            value={manualId}
            placeholder={requestedId ?? 'open a session you captured'}
            onChange={(event) => setManualId(event.target.value)}
            className="mt-1 block w-72 rounded border border-charcoal-500/30 px-2 py-1 font-mono text-xs"
          />
        </label>
        <button
          type="submit"
          data-testid="review-session-load"
          className="rounded bg-charcoal px-3 py-1.5 text-sm font-semibold text-chalk"
        >
          Load session
        </button>
        <Link
          to="/capture"
          className="text-xs text-copper-600 underline"
          data-testid="review-to-capture"
        >
          capture something first
        </Link>
      </form>

      {loading && (
        <p className="text-sm text-charcoal-500" data-testid="review-loading">
          Loading session…
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm font-medium text-red-700" data-testid="review-error">
          {error}
        </p>
      )}

      {!loading && session && clips.length === 0 && (
        <p className="text-sm text-charcoal-500" data-testid="review-empty">
          This session has no proposed clips yet. Capture a session and let the pipeline propose
          some.
        </p>
      )}

      {clips.length > 0 && <CardDeck token={token} />}
    </div>
  );
}
